import { useState } from 'react';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import GppGoodRoundedIcon from '@mui/icons-material/GppGoodRounded';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import { useApprovePrice, usePriceApprovals, useRejectPrice } from '../../hooks';
import { formatPrice } from '../../lib/format';
import type { GovernedPriceResponse } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';

interface PanelProps {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/** Price-approval queue — prices held for review; approve (→ ACTIVE) or reject (→ REJECTED). */
export function PriceApprovalsPanel({ canManage, onResult }: PanelProps) {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [rejectTarget, setRejectTarget] = useState<GovernedPriceResponse | null>(null);
  const [reason, setReason] = useState('');

  const query = usePriceApprovals({ page, size });
  const approve = useApprovePrice();
  const reject = useRejectPrice();
  const items = query.data?.content ?? [];

  if (query.isLoading) return <LoadingState label="Загрузка очереди…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Не удалось загрузить очередь"
        message={query.error?.message ?? 'Произошла ошибка.'}
        onRetry={() => void query.refetch()}
      />
    );
  }
  if (items.length === 0) {
    return (
      <EmptyState
        title="Очередь пуста"
        description="Нет цен, ожидающих утверждения."
        icon={<GppGoodRoundedIcon />}
      />
    );
  }

  return (
    <>
      <TablePageShell
        footer={
          <CompactTablePagination
            count={query.data?.totalElements ?? 0}
            page={page}
            rowsPerPage={size}
            hideRowsPerPage
            onPageChange={(_e, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          />
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Товар</TableCell>
                <TableCell align="right">Цена</TableCell>
                <TableCell align="right">Прежняя</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Причина</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {item.productId}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatPrice(item.value)}</TableCell>
                  <TableCell align="right">{formatPrice(item.previousActiveValue)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color="warning"
                      label={item.priceStatus || item.state}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {item.suspicionReason ?? item.reason ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<CheckCircleRoundedIcon />}
                        disabled={!canManage || approve.isPending}
                        onClick={() =>
                          approve.mutate(item.id, {
                            onSuccess: () => onResult('success', 'Цена утверждена.'),
                            onError: (error) => onResult('error', error.message),
                          })
                        }
                      >
                        Утвердить
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        color="error"
                        disabled={!canManage}
                        onClick={() => {
                          setRejectTarget(item);
                          setReason('');
                        }}
                      >
                        Отклонить
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TablePageShell>

      <Dialog
        open={rejectTarget !== null}
        onClose={() => setRejectTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Отклонить цену?</DialogTitle>
        <DialogContent>
          <TextField
            label="Причина (необязательно)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)} disabled={reject.isPending}>
            Отмена
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={reject.isPending}
            onClick={() => {
              if (!rejectTarget) return;
              reject.mutate(
                { priceId: rejectTarget.id, reason: reason.trim() || undefined },
                {
                  onSuccess: () => {
                    setRejectTarget(null);
                    onResult('success', 'Цена отклонена.');
                  },
                  onError: (error) => onResult('error', error.message),
                },
              );
            }}
          >
            Отклонить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
